export type OpenStep =
  /** Nothing to do yet: no request, nothing loaded, or the table is not ready. */
  | { do: 'wait' }
  /** The test is not on this website. Drop the request. */
  | { do: 'drop' }
  | { do: 'show-table' }
  | { do: 'show-view'; view: 'All' | 'Archived' }
  | { do: 'clear-filters' }
  | { do: 'clear-name-search' }
  | { do: 'clear-table-search' }
  | { do: 'go-to-page'; page: number }
  /** The row is on the page the table is showing: open it and finish. */
  | { do: 'open' };

type PlanOpenParams = {
  target: number | null;
  rows: readonly { id: number; archived?: unknown }[];
  /** The table has set its search keys. Before that its own first effect resets the page. */
  tableReady: boolean;
  layout: 'table' | 'timeline';
  view: string;
  hasLearningFilter: boolean;
  /** The legacy name filter in the list hook (?searchText=). */
  nameSearch: string;
  /** The table's own search box. */
  tableSearch: string;
  /** The ids the table matches, in the order it shows them, before paging. */
  matching: readonly { id: number }[] | null | undefined;
  currentPage: number;
  pageSize: number;
};

/**
 * The next thing to do to bring one test's learnings into view, or to stop.
 *
 * Whatever hides the row has to go first, one thing per call: the timeline layout, a
 * status view that does not hold it, a filter, either search. Each change re-renders and the
 * caller asks again, so the answer only ever depends on what is on screen now. `show-view` also
 * clears the learnings filters in the list hook, which is why the view step comes before the filter
 * one: asking for it can resolve the filters too, and the next call simply finds nothing to clear.
 *
 * The page is asked for and then waited for. The table resets itself to page one when its search
 * keys are first set and when a search changes, so a page chosen alongside either would be undone.
 * `open` is only the answer once `currentPage` is the page the row is on, and until the table is
 * ready nothing is chosen at all.
 *
 * Pure so the sequence can be tested without rendering the Library.
 */
export function planOpen({
  target,
  rows,
  tableReady,
  layout,
  view,
  hasLearningFilter,
  nameSearch,
  tableSearch,
  matching,
  currentPage,
  pageSize,
}: PlanOpenParams): OpenStep {
  if (target === null || rows.length === 0 || !tableReady) return { do: 'wait' };

  const row = rows.find((candidate) => candidate.id === target);
  if (!row) return { do: 'drop' };

  const wanted = row.archived ? 'Archived' : 'All';
  if (layout !== 'table') return { do: 'show-table' };
  if (view !== wanted) return { do: 'show-view', view: wanted };
  if (hasLearningFilter) return { do: 'clear-filters' };
  if (nameSearch) return { do: 'clear-name-search' };
  if (tableSearch) return { do: 'clear-table-search' };

  const index = matching?.findIndex((candidate) => candidate.id === target) ?? -1;
  // The table has not caught up with the change just made. It will render again.
  if (index < 0) return { do: 'wait' };

  const page = Math.floor(index / pageSize) + 1;
  return currentPage === page ? { do: 'open' } : { do: 'go-to-page', page };
}

type WebsiteId = string | number | null;

/**
 * Whether an open request should be dropped because the website changed.
 *
 * True only for a real switch: from one website to another. Not for the first resolve (the screen
 * mounts with no website and then learns which, and a `?open=` in the URL is meant for exactly
 * that one), and not when the screen is left, which is not a switch at all.
 */
export const websiteSwitched = ({ previous, current }: { previous: WebsiteId; current: WebsiteId }): boolean =>
  previous !== null && previous !== current;
